import React, { useCallback, useContext, useEffect } from "react";
import { commitMutation, graphql } from "react-relay";

import { faBolt } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { noop } from "lodash";

import ThemeContext from "~/common/ThemeContext";
import { ReactComponent as ZapierLogo } from "~/legacy-assets/images/logos/zapier.svg";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { environment } from "~/models";
import { Button } from "~/ui";
import { Modal } from "~/ui/legacyFellowIndex";
import { __globalModalContext, useModals } from "~/ui/modals";
import toast from "~/ui/toast";

import Settings from "../../../account/Integrations/Settings";

import type { ZapierIntegrationDisconnectMutation as DisconnectZapierType } from "~/__generated__/ZapierIntegrationDisconnectMutation.graphql";
import type { ZapierIntegrationQuery } from "~/__generated__/ZapierIntegrationQuery.graphql";

import styles from "./Zapier.module.css";

declare global {
	// eslint-disable-next-line @typescript-eslint/no-namespace
	namespace JSX {
		interface IntrinsicElements {
			"zapier-zap-templates": {
				apps: string;
				limit: number;
				"link-target": "new-tab" | "same-tab";
				theme: "dark" | "light" | "auto";
				categories?: string;
				presentation?: "row" | "card";
			};
		}
	}
}

const DisconnectZapierMutation = graphql`
	mutation ZapierIntegrationDisconnectMutation($input: DisconnectZapierInput!) {
		disconnectZapier(input: $input) {
			ok
		}
	}
`;

interface DisconnectModalProps {
	onClose: () => void;
}

const DisconnectModal: React.FunctionComponent<DisconnectModalProps> = props => {
	const { onClose } = props;

	const onDisconnect = React.useCallback(() => {
		commitMutation<DisconnectZapierType>(environment, {
			mutation: DisconnectZapierMutation,
			variables: {
				input: {},
			},
			onCompleted: ({ disconnectZapier }) => {
				if (disconnectZapier?.ok) {
					toast.success(`Zapier no longer has access to your account.`);
				} else {
					toast.error(`Sorry, something went wrong and we couldn't disconnect Zapier.`);
				}
			},
		});

		onClose();
	}, [onClose]);

	return (
		<Modal onClose={onClose} width="31.25em">
			<Modal.Header noBottomBorder onClose={onClose}>
				<Modal.Title>Are you sure you want to disconnect Zapier?</Modal.Title>
			</Modal.Header>
			<Modal.Body textOnly className={styles.modalBody}>
				Disconnecting Zapier will cause any existing Zaps to stop working.
			</Modal.Body>
			<Modal.Footer>
				<Button size="normal" onClick={onClose}>
					Cancel
				</Button>
				<Button size="normal" variant="destructive" onClick={onDisconnect}>
					Disconnect
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

function ZapierSettings({ onBackClick }: { onBackClick?: () => void }) {
	const [fetchKey, setFetchKey] = React.useState(0);
	const refetch = useCallback(() => setFetchKey(key => key + 1), []);
	const { viewer } = useLazyLoadQuery<ZapierIntegrationQuery>(
		graphql`
			query ZapierIntegrationQuery {
				viewer {
					account {
						billingAccount {
							hasAccessToZapierIntegration: hasAccessToFeature(feature: zapier_integration)
						}
					}
					integrations {
						zapier {
							isConnected
						}
					}
				}
			}
		`,
		{},
		{
			fetchPolicy: "store-and-network",
			fetchKey,
		},
	);

	useEffect(() => {
		/*
		 * See: https://zapier.com/partner/embed/zap-templates/create#type=popular&app=fellow
		 *
		 * This is a Vanilla JS + React solution: Vanilla JS is used to load scripts and css.
		 */

		// Load JS
		const script = document.createElement("script");
		script.type = "module";
		script.src = "https://cdn.zapier.com/packages/partner-sdk/v0/zapier-elements/zapier-elements.esm.js";
		document.head.appendChild(script);

		// Load CSS
		const stylesheet = document.createElement("link");
		stylesheet.rel = "stylesheet";
		stylesheet.href = "https://cdn.zapier.com/packages/partner-sdk/v0/zapier-elements/zapier-elements.css";
		document.head.appendChild(stylesheet);
		return () => {
			if (script != undefined) {
				document.head.removeChild(script);
			}
			if (stylesheet != undefined) {
				document.head.removeChild(stylesheet);
			}
		};
	}, []);

	const modalContext = useModals();
	const { isDarkMode } = useContext(ThemeContext);

	const onDisconnectModalOpen = useCallback(async () => {
		await modalContext.open(({ close }) => <DisconnectModal onClose={close} />);
		refetch();
	}, [modalContext, refetch]);

	const onConnect = useCallback(() => window.open("https://zapier.com", "_blank", "noopener noreferrer"), []);

	const { integrations, account } = viewer;
	const connected = Boolean(integrations.zapier?.isConnected);
	const paywalled = !account.billingAccount.hasAccessToZapierIntegration;

	return (
		<React.Fragment>
			<Settings
				name="Zapier"
				connected={connected}
				logo={<ZapierLogo height="24px" />}
				paywalledButton={paywalled ? "fellow_pro_ai" : undefined}
				paywalledBanner={paywalled ? "fellow_pro_ai" : undefined}
				paywallFeature="zapier_integration"
				installText="Connect"
				tabs={[]}
				activeTab={""}
				onTabClick={noop}
				onInstall={onConnect}
				onDisconnect={onDisconnectModalOpen}
				onBackClick={onBackClick}
				backPath="/settings/integrations/"
				installIcon={faBolt}
			>
				<div className={styles.textContainer}>
					<div className={styles.header}>
						<span className={styles.headerTitle}>About this integration</span>
					</div>
					<div className={styles.settingsText}>
						<p>Connect your favourite apps and automate your workflow using our Zapier integration.</p>
						<p>
							With Fellow’s Zapier integration, you can push action items to your favourite apps and pull in talking
							points, action items, and meeting notes from thousands of applications including Trello, Airtable,
							Discord, Todoist, Twilio, Evernote, and more.
						</p>
					</div>
					<span className={styles.templatesTitle}>Get started with templates on Zapier</span>
				</div>
				<zapier-zap-templates apps="fellow" limit={10} link-target="new-tab" theme={isDarkMode ? "dark" : "light"} />
			</Settings>
		</React.Fragment>
	);
}

export default ZapierSettings;
